// src/components/TagPills/TagPills.tsx

import React from 'react'
import { AnnotationDataBoundingBox } from '../types/models/annotation'
import { getContrastYIQ } from '../utils'
import { useGetTagsQuery } from '../services/tagApi'
import { useAppSelector } from '../store/hooks'
import { selectSelectedProjectId } from '../store/slices/projectSlice'
import { useGetProjectQuery } from '../services/projectsApi'

interface BBoxPillsProps {
  bounding_boxes: AnnotationDataBoundingBox[]
  showTagCounts?: boolean
  maxTags?: number
  direction?: 'row' | 'column'
}

const PillsBBox: React.FC<BBoxPillsProps> = ({
  bounding_boxes,
  showTagCounts = true,
  maxTags = 3,
  direction = 'row',
}) => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const { data: tags } = useGetTagsQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
    },
    { skip: !selectedProjectId || !project?.datasetid }
  )

  // Early return if no valid bounding boxes
  if (!bounding_boxes || bounding_boxes.length === 0) {
    return null
  }

  const tagCounts = bounding_boxes.reduce((acc, bbox) => {
    if (bbox && bbox.name && bbox.name.trim() !== '') {
      acc[bbox.name] = (acc[bbox.name] || 0) + 1
    }
    return acc
  }, {} as Record<string, number>)

  const uniqueTags = Array.from(
    new Set(bounding_boxes.map((bbox) => bbox.name).filter((name) => name && name.trim() !== ''))
  )

  // Return null if no valid tags found
  if (uniqueTags.length === 0) {
    return null
  }

  const sortedTags = uniqueTags.sort((t1, t2) => (tagCounts?.[t2] || 0) - (tagCounts?.[t1] || 0))
  const visibleTags = sortedTags.slice(0, maxTags)
  const remainingCount = sortedTags.length - maxTags

  const renderPlusTag = (remainingCount: number) => {
    if (remainingCount <= 0) return null
    return (
      <div>
        <div
          style={{
            userSelect: 'none',
            background: 'linear-gradient(135deg, #402563 0%, #114ee0 100%)',
            color: '#ffffff',
            padding: '0.25rem 0.5rem',
            height: '1.2rem',
            borderRadius: '9999px',
            fontSize: '0.75rem',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <span>+{remainingCount}</span>
        </div>
      </div>
    )
  }

  const isColumn = direction === 'column'

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: isColumn ? 'nowrap' : 'wrap',
        flexDirection: isColumn ? 'column' : 'row',
        gap: '0.5rem',
      }}
    >
      {visibleTags.map((tagId, index) => {
        const tag = tags?.tags?.find((t) => t.id === tagId || t.name === tagId)
        if (!tag) return null

        const backgroundColor = `#${tag.color.toString(16).padStart(6, '0').slice(0, 6)}`
        const textColor = getContrastYIQ(backgroundColor)

        return (
          <div key={`${tag.id}-${index}`}>
            <div
              key={index}
              style={{
                userSelect: 'none',
                backgroundColor,
                color: textColor,
                padding: '0.25rem 0.5rem',
                height: '1.2rem',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <span key={`tag-${index}`}>{tag.name}</span>
              {showTagCounts && tagCounts?.[tagId] && (
                <span
                  key={`span-${index}`}
                  style={{
                    userSelect: 'none',
                    backgroundColor: textColor,
                    color: backgroundColor,
                    borderRadius: '9999px',
                    width: '1.25rem',
                    height: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: '0.25rem',
                    fontSize: '0.7rem',
                  }}
                >
                  {tagCounts[tagId]}
                </span>
              )}
            </div>
          </div>
        )
      })}
      {remainingCount > 0 && renderPlusTag(remainingCount)}
    </div>
  )
}

export default PillsBBox
